import { Inbox, Lock } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHead } from '@/admin-area/ui/PageHead';
import { StateMessage } from '@/admin-area/ui/StateMessage';
import { ViewTabs, type ListView } from '@/components/list/ViewTabs';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { QueueCards, QueueTable } from './QueueList';
import { useApprovalQueue } from './use-approval-queue';
import { useOnboardingClick } from '@/communications/use-onboarding-click';

type Level = 'first' | 'second';

/**
 * «Da approvare» (spec §1.4, §1.5): the reports the caller may decide now, by level. The 2nd-level
 * tab appears only when something waits there: the organisation's setting is not readable by an
 * approver, so a one-approver organisation simply never has a 2nd level.
 */
export function ApprovalsPage() {
  // The email's button lands here with `?onboarding=<step>`: logged once, then removed (#739).
  useOnboardingClick();
  const { t } = useTranslation('expenses');
  const { t: tr } = useTranslation('expenses-reports');
  const titleId = useId();
  const { state, reload } = useApprovalQueue();
  const [chosen, setChosen] = useState<Level>('first');

  let body;
  if (state.status === 'loading') {
    body = (
      <SkeletonRegion label={t('list.loading')} className="flex flex-col gap-2">
        {[0, 1, 2, 3].map((n) => (
          <Skeleton key={n} className="h-12 w-full" />
        ))}
      </SkeletonRegion>
    );
  } else if (state.status === 'forbidden') {
    body = (
      <StateMessage
        role="alert"
        icon={Lock}
        title={t('approvals.noAccess')}
        text={t('approvals.noAccessText')}
      />
    );
  } else if (state.status === 'error') {
    body = (
      <StateMessage
        role="alert"
        tone="error"
        icon={Inbox}
        title={t('approvals.loadError')}
        action={
          <Button type="button" variant="outline" onClick={reload}>
            {t('approvals.retry')}
          </Button>
        }
      />
    );
  } else {
    const { firstLevel, secondLevel } = state.queue;
    const hasSecond = secondLevel.count > 0 || secondLevel.items.length > 0;
    const level: Level = hasSecond ? chosen : 'first';
    const items = (level === 'first' ? firstLevel : secondLevel).items;
    const counts: Record<Level, number> = { first: firstLevel.count, second: secondLevel.count };
    const levels: ListView[] = (['first', 'second'] as const).map((key) => ({
      id: key,
      label: t(`approvals.${key}`, { count: counts[key] }),
      params: { level: key },
    }));
    body = (
      <>
        {hasSecond ? (
          <ViewTabs
            label={tr('approvals.levels')}
            views={levels}
            current={{ get: (key) => (key === 'level' ? level : null) }}
            onSelect={(view) => setChosen(view.id as Level)}
          />
        ) : null}
        {items.length === 0 ? (
          <StateMessage
            icon={Inbox}
            title={tr('approvals.empty.title')}
            text={tr('approvals.empty.text')}
          />
        ) : (
          <>
            <QueueTable items={items} />
            <QueueCards items={items} />
          </>
        )}
      </>
    );
  }

  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-4">
      <PageHead titleId={titleId} title={t('approvals.title')} summary={tr('approvals.scope')} />
      {body}
    </section>
  );
}
